import _ from 'lodash';
import { colors, createMuiTheme, responsiveFontSizes } from '@material-ui/core';
import { Theme as MuiTheme } from '@material-ui/core/styles/createMuiTheme';
import { Shadows as MuiShadows } from '@material-ui/core/styles/shadows';
import {
  Palette as MuiPalette,
  TypeBackground as MuiTypeBackground
} from '@material-ui/core/styles/createPalette';
import { THEMES, COLORS } from 'src/constants';
import { softShadows, strongShadows } from './shadows';
import typography from './typography';

interface TypeBackground extends MuiTypeBackground {
  dark: string;
}

interface Palette extends MuiPalette {
  background: TypeBackground;
}

export interface Theme extends MuiTheme {
  name: string;
  palette: Palette;
}

type Direction = 'ltr' | 'rtl';

interface ThemeConfig {
  direction?: Direction;
  responsiveFontSizes?: boolean;
  theme?: string;
}

interface ThemeOptions {
  name?: string;
  direction?: Direction;
  typography?: Record<string, any>;
  overrides?: Record<string, any>;
  palette?: Record<string, any>;
  shadows?: MuiShadows;
}

const baseOptions: ThemeOptions = {
  direction: 'ltr',
  typography,
  overrides: {
    MuiLinearProgress: {
      root: {
        borderRadius: 3,
        overflow: 'hidden'
      }
    },
    MuiListItemIcon: {
      root: {
        minWidth: 32
      }
    },
    MuiChip: {
      root: {
        backgroundColor: 'rgba(0,0,0,0.075)'
      }
    }
  }
};

const themesOptions: ThemeOptions[] = [
  {
    name: THEMES.LIGHT,
    overrides: {
      MuiInputBase: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: colors.blueGrey[600]
          }
        }
      }
    },
    palette: {
      type: 'light',
      action: {
        active: colors.blueGrey[600]
      },
      background: {
        default: colors.common.white,
        dark: '#F2F5F7',
        paper: colors.common.white
      },
      primary: {
        main: COLORS.UI_CYAN,
        dark: COLORS.UI_CYAN_DARK,
        contrastText: colors.common.white
      },
      // Same hue as primary by design: this is a single-accent brand, and
      // `secondary` reads as "selected/highlight" across the app (date picker,
      // retail calendar, labels, active nav). Splitting it onto the coral would
      // change those semantics, not just their colour.
      secondary: {
        main: COLORS.UI_CYAN,
        dark: COLORS.UI_CYAN_DARK,
        contrastText: colors.common.white
      },
      // Status is deliberately separate from the brand accent so a red figure
      // reads as a real drop rather than as decoration.
      success: {
        main: '#1F7A4D',
        contrastText: colors.common.white
      },
      error: {
        main: '#C9381A',
        contrastText: colors.common.white
      },
      // Amber: 5.1:1 on white, so "near
      // capacity" is legible (MUI's default #ff9800 is 2.2:1).
      warning: {
        main: '#9A6200',
        contrastText: colors.common.white
      },
      text: {
        primary: colors.blueGrey[900],
        secondary: colors.blueGrey[600]
      }
    },
    shadows: softShadows
  },
  {
    name: THEMES.ONE_DARK,
    overrides: {
      MuiToolbar: {
        root: {
          color: '#e6e5e8'
        }
      }
    },
    palette: {
      type: 'dark',
      action: {
        active: 'rgba(255, 255, 255, 0.54)',
        hover: 'rgba(255, 255, 255, 0.04)',
        selected: 'rgba(255, 255, 255, 0.08)',
        disabled: 'rgba(255, 255, 255, 0.26)',
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
        focus: 'rgba(255, 255, 255, 0.12)'
      },
      // Canvas / chrome / card were within a few steps of each other and slightly
      // warm, so nothing read as raised. These are spread far enough apart to build
      // a real elevation ladder, on a cooler neutral so cyan sits on top of it.
      background: {
        default: '#0D151B',
        dark: '#0A1015',
        paper: '#131C24'
      },
      divider: '#22303B',
      primary: {
        main: COLORS.UI_CYAN_ON_DARK,
        dark: COLORS.UI_CYAN,
        contrastText: '#06212C'
      },
      secondary: {
        main: COLORS.UI_CYAN_ON_DARK,
        dark: COLORS.UI_CYAN,
        contrastText: '#06212C'
      },
      success: {
        main: '#4FC98B',
        contrastText: '#06212C'
      },
      error: {
        main: '#FF7A62',
        contrastText: '#2A0B04'
      },
      warning: {
        main: '#E3A93A',
        contrastText: '#2A1C02'
      },
      text: {
        primary: '#E9EFF4',
        secondary: '#93A6B3'
      }
    },
    shadows: strongShadows
  }
];

export const createTheme = (config: ThemeConfig = {}): Theme => {
  let themeOptions = themesOptions.find(theme => theme.name === config.theme);

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    [themeOptions] = themesOptions;
  }

  let theme = createMuiTheme(
    _.merge({}, baseOptions, themeOptions, { direction: config.direction })
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme as Theme;
};
